import { HTMLAttributes, PropsWithChildren } from "react"
import { List as RMWCList, ListProps as RWMCListProps } from "@rmwc/list"
import omit from "lodash.omit"
import { MagnifyingGlassIcon, CrossCircledIcon } from "../Icons"
import { styled, StyleProps, CSS, CSSFn, StylePropsWithCSS, callCSSFn } from "../styles"
import { Direction, Variant } from "./types"

export const Container = styled.div<HTMLAttributes<HTMLDivElement>>(() => ({
  position: `relative`,
  boxSizing: `content-box`,
}))

export const Relative = styled.div<HTMLAttributes<HTMLDivElement> & { css?: CSSFn }>((props: StylePropsWithCSS) => ({
  position: `relative`,
  ...callCSSFn(props),
}))

const iconStyle: CSS = {
  color: `#000`,
  display: `block`,
  position: `absolute`,
  top: `50%`,
  transform: `translateY(-50%)`,
}

export const SearchIcon = styled(MagnifyingGlassIcon)(
  (props: StyleProps): CSS => ({
    ...iconStyle,
    left: props.theme.spacing(5),
  })
)

export const ClearIcon = styled(CrossCircledIcon)(
  (props: StyleProps): CSS => ({
    ...iconStyle,
    right: props.theme.spacing(5),
    cursor: `pointer`,
  })
)

type Props = {
  direction: Direction
  variant: Variant
}

type ListProps = PropsWithChildren<RWMCListProps & Props>

const ListComponent = (props: ListProps) => <RMWCList {...omit(props, `direction`, `variant`)} />

const getFont = (props: StyleProps & Props) => {
  switch (props.variant) {
    case `filter`:
    default:
      return { ...props.theme.fonts.body }
  }
}

export const List = styled(ListComponent)(
  (props: StyleProps & Props): CSS => ({
    ...getFont(props),
    fontSize: `0.938rem`,
    position: `absolute`,
    border: `1px #bcbbb8 solid`,
    minWidth: 178,
    right: props.direction === `up` ? undefined : 0,
    borderRadius: `7px`,
    zIndex: 2,
    bottom: props.direction === `up` ? `100%` : `auto`,
    top: props.direction === `down` ? `100%` : `auto`,
    marginTop: props.direction === `down` ? `5px` : 0,
    marginBottom: props.direction === `up` ? `5px` : 0,
    maxHeight: 300,
    background: props.theme.palette.white,
    boxShadow: `0px 2px 0px 0px rgba(0,0,0,0.15);`,
    color: props.theme.palette.coal.dark,
    textWrap: `nowrap`,
  })
)

type FilterItemProps = { selected: boolean; isLast?: boolean }
export const FilterItem = styled.div<FilterItemProps>((props: StyleProps & FilterItemProps): CSS => {
  const psuedo = {
    content: `""`,
    position: `absolute`,
    left: 0,
    top: `50%`,
    transform: `translateY(-50%)`,
  }
  const before = props.isLast
    ? { ...psuedo, height: `12.5px`, left: `6.25px`, border: `0.5px solid ${props.theme.palette.grey.dark}` }
    : {
        ...psuedo,
        height: `14px`,
        width: `14px`,
        borderRadius: `50%`,
        border: `1px solid ${props.theme.palette.grey.dark}`,
      }
  const style = {
    paddingLeft: `20px`,
    position: `relative`,
    "&:before": before,
  }
  if (props.isLast)
    return {
      ...style,
      "&: after": {
        ...psuedo,
        left: `0.75px`,
        width: `12.5px`,
        border: `0.5px solid ${props.theme.palette.grey.dark}`,
      },
    } as CSS

  if (!props.selected) return style as CSS
  return {
    ...style,
    "&:after": {
      ...psuedo,
      left: `3px`,
      height: `8px`,
      width: `8px`,
      borderRadius: `50%`,
      background: props.theme.palette.info,
    },
  } as CSS
})

export const Separator = styled.hr((props: StyleProps) => ({
  color: props.theme.palette.grey.dark,
  margin: props.theme.spacing(1, 4),
}))

export const StyledFilter = styled.div<HTMLAttributes<HTMLDivElement> & { css?: CSSFn }>(
  (props: StylePropsWithCSS) => ({
    display: `flex`,
    justifyContent: `center`,
    alignItems: `center`,
    flexDirection: `row`,
    columnGap: props.theme.spacing(3),
    ...callCSSFn(props),
  })
)

export const StyledLabel = styled.p<HTMLAttributes<HTMLElement> & { css?: CSSFn }>((props: StylePropsWithCSS) => ({
  fontSize: `0.875rem`,
  textAlign: `right`,
  margin: `0 0 0 0.5rem`,
  ...callCSSFn(props),
}))

export const StyledChevronUp = styled.div<HTMLAttributes<HTMLDivElement> & { css?: CSSFn }>(() => ({
  marginLeft: `0.5rem`,
  position: `relative`,
  top: `1px`,
  transform: `rotateX(180deg)`,
}))

export const StyledChevronDown = styled.div<HTMLAttributes<HTMLDivElement> & { css?: CSSFn }>(() => ({
  marginLeft: `0.5rem`,
  position: `relative`,
  bottom: `1px`,
}))
